import React, { useEffect, useState } from 'react';
import styles from './Notes.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import RichTextField from '../../../../components/rich-text/RichTextField';
import RichTextView from '../../../../components/rich-text/RichTextView';
import { toRichText, isRichTextEmpty, richTextToPlain } from '../../../../components/rich-text/richText';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import DateChip from '../moduleCard/DateChip';
import CalendarConfig from '../../../../consts/CalendarConfig';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { findAppointmentNote } from '../../modules/appointmentModules';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import SaveIcon from '@mui/icons-material/Save';
import { Chip } from '@mui/material';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildNotesJob } from '../../modules/reportSections';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.NOTES];

/**
 * Módulo "Anotações": um campo livre por atendimento, com formatação básica
 * (negrito, itálico, sublinhado, listas) gravada como HTML; anotações antigas
 * em texto puro continuam valendo. Durante a consulta o campo
 * edita a anotação deste atendimento (rascunho guardado no localStorage, como o
 * registro de queixa/evolução); as anotações de consultas anteriores ficam
 * listadas abaixo. Nunca é obrigatória: o botão "Finalizar" grava o que houver.
 *
 * `onChange` informa a página do texto atual (gravado ao registrar a consulta);
 * `onSaved` é chamado após o salvamento manual pelo botão do card.
 * `patientName`, `professionalId`, `companyConfig`: identificação no PDF das
 * anotações gravadas (o rascunho em edição não entra).
 */
const Notes = ({ list, appointmentId, patientId, enabled, collapsible = false, onChange, onSaved, patientName, professionalId, companyConfig }) => {
  const { SetPatientNote } = DataProviderInstance();
  const notes = Array.isArray(list) ? list : [];
  const currentNote = enabled ? findAppointmentNote(notes, appointmentId) : null;
  const savedText = currentNote ? currentNote.note : '';
  const savedHtml = toRichText(savedText);
  const previousNotes = enabled ? notes.filter((note) => note !== currentNote) : notes;

  const DRAFT_KEY = appointmentId ? `${appointmentId}_note` : null;
  const readDraft = () => (DRAFT_KEY ? localStorage.getItem(DRAFT_KEY) : null);

  const [text, setText] = useState(() => {
    const draft = readDraft();
    return toRichText(draft !== null && draft !== '' ? draft : savedText);
  });
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Editor vazio guarda `<p><br></p>`: vale como sem anotação
  const normalize = (value) => (isRichTextEmpty(value) ? '' : value);
  const isDirty = normalize(text) !== normalize(savedHtml);

  // Texto gravado chegou (reload após salvar): adota e descarta o rascunho
  useEffect(() => {
    if (!enabled) return;
    const draft = readDraft();
    if (draft === null || draft === '' || toRichText(draft) === savedHtml) {
      setText(savedHtml);
      if (DRAFT_KEY) localStorage.removeItem(DRAFT_KEY);
    }
  }, [savedText, enabled]);

  // Sem edição, devolve o texto gravado como está: uma anotação antiga em texto
  // puro não é regravada em HTML só por ter sido aberta
  useEffect(() => {
    if (onChange) {
      onChange(isDirty ? normalize(text) : savedText);
    }
  }, [text, savedText]);

  function handleTextChange(value) {
    setText(value);
    if (DRAFT_KEY) {
      localStorage.setItem(DRAFT_KEY, value);
    }
  }

  function handleSave() {
    if (!patientId || !appointmentId) return;
    setSaving(true);
    SetPatientNote(patientId, appointmentId, normalize(text), (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        if (DRAFT_KEY) localStorage.removeItem(DRAFT_KEY);
        setLastSaved(new Date());
        if (onSaved) onSaved();
      }
    });
  }

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildNotesJob(notes, appointmentId),
  });

  function renderNoteItem(note) {
    return (
      <div key={note.id} className={parts.item}>
        <div className={parts.itemRow}>
          <DateChip label={CalendarConfig.GetDDMMAAAA(note.createdAt)} />
        </div>
        <RichTextView value={note.note} className={parts.itemText} />
      </div>
    );
  }

  const actions = (
    <>
      {notes.length > 0 && <ModulePdfButton report={report} />}
      {enabled && lastSaved && (
        <Chip
          icon={<SaveIcon fontSize="small" />}
          label={`Salvo às ${lastSaved.toLocaleTimeString()}`}
          size="small"
          color="success"
          variant="outlined"
        />
      )}
      {enabled && (
        <CustomButton
          style="primary"
          variant="outlined"
          icon={<SaveIcon />}
          label="Salvar anotação"
          enabled={isDirty && !saving}
          loading={saving}
          onClick={handleSave}
        />
      )}
    </>
  );

  return (
    <>
    <ModuleCard
      accent="amber"
      title="Anotações"
      icon={<StickyNote2OutlinedIcon />}
      description={enabled ? 'Anotações livres deste atendimento' : `Anotações dos atendimentos (${notes.length})`}
      actions={actions}
      collapsible={collapsible}
    >
      {enabled && (
        <div className={styles.editor}>
          <RichTextField
            value={text}
            onChange={handleTextChange}
            placeholder="Escreva aqui o que for importante deste atendimento"
          />
          <div className={styles.charCount}>{richTextToPlain(text).trim().length} caracteres</div>
        </div>
      )}

      {previousNotes.length === 0 ? (
        !enabled && <ModuleEmptyState icon={<StickyNote2OutlinedIcon />} text="Não existem anotações registradas." />
      ) : (
        <div className={parts.section}>
          <span className={parts.sectionLabel}>
            {enabled ? `Anotações anteriores (${previousNotes.length})` : `Histórico de anotações (${previousNotes.length})`}
          </span>
          <div className={parts.scrollList}>
            {previousNotes.map(renderNoteItem)}
          </div>
        </div>
      )}
    </ModuleCard>

    {report.job && (
      <HiddenReport>
        <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
      </HiddenReport>
    )}
    </>
  );
};

export default Notes;
